import type { LocalDatabaseCustomer } from "../../../_Generated/Sync.gen";
import { union } from "../../../Utilities/Generic/Arrays";
import { SearchCommunicator } from "../../../Workers/Search/SearchCommunicator";
import { SearchIndexes } from "../../../Workers/Search/SearchEvents";
import type {
    BaseCustomerRepository,
    CustomerRepositorySearchOptions,
} from "../../_Bases/BaseCustomerRepository";
import type { InternalIndexedDBDatabase } from "../../DataStores/Database";
import BaseIndexedDBRepository from "../../Implementations/BaseIndexedDBRepository";

export default class IndexedDBCustomerRepository extends BaseIndexedDBRepository<
    LocalDatabaseCustomer
> implements BaseCustomerRepository {
    public source: InternalIndexedDBDatabase["customers"];
    protected readonly dataTypeName = "Customer";

    constructor(source: InternalIndexedDBDatabase) {
        super();

        this.source = source.customers;
    }

    protected unionResults<T>(arrays: Array<Array<T>>): Array<T> {
        let unioned = arrays[0];

        for(let i = 1, l = arrays.length; i < l; i++) {
            unioned = union(unioned, arrays[i]);
        }

        return unioned;
    }

    protected getOptions(options?: Partial<CustomerRepositorySearchOptions>): CustomerRepositorySearchOptions {
        const defaultOptions: CustomerRepositorySearchOptions = {
            name          : true,
            code          : false,
            email         : true,
            phone         : true,
            exactMatch    : false,
            company       : true,
            customerGroups: [],
            sort          : false,
            filter        : undefined,
            limit         : Infinity,
        };

        let newOptions;

        if(options) {
            newOptions = Object.assign({}, defaultOptions, options);
        } else {
            newOptions = defaultOptions;
        }

        if(!newOptions.name && !newOptions.code && !newOptions.email && !newOptions.phone) {
            throw new Error("Not searching for anything");
        }

        return newOptions;
    }

    protected async wildcardSearch(
        value: string,
        options: CustomerRepositorySearchOptions
    ): Promise<Array<LocalDatabaseCustomer>> {

        const results = [];

        if(options.name) {
            const searchResults = await SearchCommunicator.search(SearchIndexes.CUSTOMER_NAME, value);

            for(let i = 0, l = searchResults.length; i < l; i++) {
                results.push(searchResults[i]);
            }
        }

        if(options.company) {
            const searchResults = await SearchCommunicator.search(SearchIndexes.CUSTOMER_COMPANY, value);

            for(let i = 0, l = searchResults.length; i < l; i++) {
                results.push(searchResults[i]);
            }
        }

        if(options.phone) {
            const searchResults = await SearchCommunicator.search(SearchIndexes.CUSTOMER_PHONE, value);

            for(let i = 0, l = searchResults.length; i < l; i++) {
                results.push(searchResults[i]);
            }
        }

        if(options.email) {
            const searchResults = await SearchCommunicator.search(SearchIndexes.CUSTOMER_EMAIL, value);

            for(let i = 0, l = searchResults.length; i < l; i++) {
                results.push(searchResults[i]);
            }
        }

        if(options.code) {
            const searchResults = await SearchCommunicator.search(SearchIndexes.CUSTOMER_CODE, value);

            for(let i = 0, l = searchResults.length; i < l; i++) {
                results.push(searchResults[i]);
            }
        }

        if(!results.length) {
            return [];
        }

        return this.source.where("uuid").anyOf(results).filter(result => {
            if(options.customerGroups.length) {
                if(!result.customer_group_id) {
                    return false;
                }

                if(!options.customerGroups.includes(result.customer_group_id)) {
                    return false;
                }
            }

            return true;
        }).limit(options.limit).sortBy("name");
    }

    protected exactSearch(
        value: string,
        options: CustomerRepositorySearchOptions
    ): Promise<Array<LocalDatabaseCustomer>> {
        const promises = [];

        if(options.name) {
            promises.push(this.source.where("name").anyOfIgnoreCase(value).toArray());
        }

        if(options.code) {
            promises.push(this.source.where("code").anyOfIgnoreCase(value).toArray());
        }

        if(options.email) {
            promises.push(this.source.where("email").anyOfIgnoreCase(value).toArray());
        }

        if(options.phone) {
            promises.push(this.source.where("phones").anyOfIgnoreCase(value).toArray());
        }

        if(options.company) {
            promises.push(this.source.where("company").anyOfIgnoreCase(value).toArray());
        }

        if(options.customerGroups.length) {
            promises.push(this.source.where("customer_group_id").anyOf(options.customerGroups).toArray());
        }

        return Promise.all(promises).then(resolved => {
            return this.unionResults(resolved);
        });
    }

    public search(
        value: string,
        options: Partial<CustomerRepositorySearchOptions>
    ): Promise<Array<LocalDatabaseCustomer>> {
        let search;

        if(options.exactMatch) {
            search = this.exactSearch(value, this.getOptions(options));
        } else {
            search = this.wildcardSearch(value, this.getOptions(options));
        }

        return search.then(results => {
            if(options.filter) {
                results = results.filter(options.filter);
            }

            if(options.sort) {
                results.sort((a, b) => {
                    return (a.first_name + a.last_name).localeCompare(b.first_name + b.last_name);
                });
            }

            return results;
        });
    }

    public getByCustomerGroup(uuid: string): Promise<Array<LocalDatabaseCustomer>> {
        return this.source
            .where("customer_group_id")
            .equals(uuid)
            .toArray();
    }

    public getByPriceList(uuid: string): Promise<Array<LocalDatabaseCustomer>> {
        return this.source
            .where("pricelist_id")
            .equals(uuid)
            .toArray();
    }

    private replaceIndexes(
        id: string,
        name?: string,
        code?: string,
        email?: string,
        phones?: Array<string>,
        company?: string
    ): void {
        SearchCommunicator.updateIndex(SearchIndexes.CUSTOMER_NAME, id, name);
        SearchCommunicator.updateIndex(SearchIndexes.CUSTOMER_CODE, id, code);
        SearchCommunicator.updateIndex(SearchIndexes.CUSTOMER_EMAIL, id, email);
        SearchCommunicator.updateIndex(SearchIndexes.CUSTOMER_PHONE, id, phones);
        SearchCommunicator.updateIndex(SearchIndexes.CUSTOMER_COMPANY, id, company);
    }

    private clearIndexes(): void {
        SearchCommunicator.emptyIndex(SearchIndexes.CUSTOMER_NAME);
        SearchCommunicator.emptyIndex(SearchIndexes.CUSTOMER_CODE);
        SearchCommunicator.emptyIndex(SearchIndexes.CUSTOMER_EMAIL);
        SearchCommunicator.emptyIndex(SearchIndexes.CUSTOMER_PHONE);
        SearchCommunicator.emptyIndex(SearchIndexes.CUSTOMER_COMPANY);
    }

    public put(item: Array<LocalDatabaseCustomer> | LocalDatabaseCustomer): Promise<string> {
        return super.put(item)
            .then(async id => {
                if(!Array.isArray(item)) {
                    item = [ item ];
                }

                for(let i = 0, l = item.length; i < l; i++) {
                    this.replaceIndexes(
                        item[i].uuid,
                        `${item[i].first_name} ${item[i].last_name}`.trim(),
                        item[i].code,
                        item[i].email,
                        item[i].phones,
                        item[i].company
                    );
                }

                return id;
            });
    }

    public delete(item: Array<string> | string): Promise<void> {
        return super.delete(item)
            .then(() => {
                if(!Array.isArray(item)) {
                    item = [ item ];
                }

                for(let i = 0, l = item.length; i < l; i++) {
                    this.replaceIndexes(item[i]);
                }
            });
    }

    public empty(): Promise<void> {
        return super.empty()
            .then(() => {
                this.clearIndexes();
            });
    }
}
